export function parseBase64Json<T>(b64: string): T {
  const decoded = atob(b64);
  return JSON.parse(decoded) as T;
}

// Type for JSON-serializable values
type JsonValue = string | number | boolean | null | JsonObject | JsonArray;

interface JsonObject {
  [key: string]: JsonValue;
}

type JsonArray = JsonValue[];

// Type for the result of parsing, which might be wrapped
type ParsedResult<T = unknown> = T | { value: T };

// Type guard to check if a value is a valid JSON value
const isJsonValue = (value: unknown): value is JsonValue => {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.every(isJsonValue);
  }
  if (typeof value === 'object' && value !== null) {
    return Object.values(value).every(isJsonValue);
  }
  return false;
};

// Helper function to try parsing a string as JSON
const tryParseString = (str: string): JsonValue | string => {
  try {
    // First attempt: direct JSON parse
    const parsed = JSON.parse(str);
    // Ensure we only return valid JSON values
    if (isJsonValue(parsed)) {
      return parsed;
    }
    return str;
  } catch {
    try {
      // Second attempt: handle escaped JSON strings
      // This handles cases where the JSON is double-stringified
      const unescaped = str.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
      const parsed = JSON.parse(unescaped);
      if (isJsonValue(parsed)) {
        return parsed;
      }
      return str;
    } catch {
      // If all parsing fails, return the original string
      return str;
    }
  }
};

// Helper function to recursively parse nested JSON strings
const recursiveParse = (data: JsonValue | string): JsonValue => {
  if (typeof data === 'string') {
    const parsed = tryParseString(data);
    // If we successfully parsed a string into an object/array, recurse
    if (typeof parsed === 'object' && parsed !== null) {
      return recursiveParse(parsed);
    }
    return parsed;
  } else if (Array.isArray(data)) {
    return data.map(recursiveParse) as JsonArray;
  } else if (typeof data === 'object' && data !== null) {
    const result: JsonObject = {};
    for (const [key, value] of Object.entries(data)) {
      result[key] = recursiveParse(value);
    }
    return result;
  }
  return data;
};

// Parse JSON data if it's a string, recursively handling nested JSON strings
export const parseJsonData = <T = unknown>(rawData: T): ParsedResult<T> => {
  // Start the recursive parsing
  if (typeof rawData === 'string') {
    const parsed = tryParseString(rawData);
    return recursiveParse(parsed) as ParsedResult<T>;
  } else if (
    typeof rawData === 'object' &&
    rawData !== null &&
    isJsonValue(rawData)
  ) {
    return recursiveParse(rawData) as ParsedResult<T>;
  }

  // For other types, wrap in an object
  return { value: rawData };
};
